import React, { useState, useEffect, useCallback } from "react";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar"; // Using Navbar to match your reference
import Swal from "sweetalert2";
import { 
  Pencil, Trash2, Plus, Search, ChevronLeft, ChevronRight, 
  MapPin, ArrowLeft, Save, Loader2, X, Phone, Mail, RotateCcw, Eye, Building2, User
} from "lucide-react";

const Branches = () => {
  // --- VIEW NAVIGATION ---
  const [view, setView] = useState("list"); 
  
  // --- DATA STATES ---
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  // --- MODAL & FORM STATES ---
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    branchName: "", branchCode: "", branchIncharge: "", address: "", city: "", phoneNumber: "", email: "", status: "Active"
  });

  // --- FETCHING LOGIC ---
  const fetchBranches = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${APIConfig.APIACTIVEURL}${APIConfig.BRANCH_GET_ALL}?pageNumber=${currentPage}&pageSize=${pageSize}&searchTerm=${searchTerm}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        setBranches([]);
        setLoading(false);
        return;
      }

      const result = await response.json();
      setBranches(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (e) { 
      console.error("Branch fetch error:", e); 
      setBranches([]);
    }
    setLoading(false);
  }, [currentPage, searchTerm]);

  useEffect(() => {
    if (view === "list") fetchBranches();
  }, [view, fetchBranches]);

  // --- HANDLERS ---
  const handleSearchTrigger = () => { 
    setSearchTerm(inputValue); 
    setCurrentPage(1); 
  };

  const handleClearSearch = () => {
    setInputValue("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleOpenCreate = () => {
    setEditingId(null);
    setFormData({ branchName: "", branchCode: "", branchIncharge: "", address: "", city: "", phoneNumber: "", email: "", status: "Active" });
    setView("manage");
  };

  const handleOpenEdit = (b) => {
    setEditingId(b.branchId);
    setFormData({ ...b });
    setView("manage");
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const method = editingId ? "PUT" : "POST";
      const url = editingId 
        ? `${APIConfig.APIACTIVEURL}${APIConfig.BRANCH_BASE}/${editingId}` 
        : `${APIConfig.APIACTIVEURL}${APIConfig.BRANCH_BASE}`;

      const res = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, branchId: editingId }),
      });

      if (res.ok) { 
        setView("list"); 
        Swal.fire("Success", editingId ? "Branch updated" : "Branch created", "success"); 
      } else {
        Swal.fire("Error", "Failed to save branch", "error");
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({ 
        title: 'Are you sure?', 
        text: "You won't be able to revert this!",
        icon: 'warning', 
        showCancelButton: true,
        confirmButtonColor: '#4f46e5',
        cancelButtonColor: '#ef4444'
    });
    if (result.isConfirmed) {
      await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.BRANCH_BASE}/${id}`, { method: "DELETE" });
      fetchBranches();
      Swal.fire("Deleted!", "Branch has been removed.", "success");
    }
  };

  // --- STYLES (Matching HolidayManager) ---
  const styles = {
    container: { padding: "40px 60px", backgroundColor: "#f4f7fe", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "35px" },
    title: { fontSize: "30px", fontWeight: "900", color: "#1b2559", margin: 0 },
    tableWrapper: { backgroundColor: "#fff", borderRadius: "20px", border: "1px solid #f1f5f9", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.02)" },
    th: { padding: "18px 24px", backgroundColor: "#f8fafc", color: "#a3aed0", fontSize: "12px", fontWeight: "800", textTransform: "uppercase", textAlign: "left" },
    td: { padding: "20px 24px", borderBottom: "1px solid #f8fafc", verticalAlign: "middle" },
    input: { width: "100%", padding: "14px 16px", borderRadius: "12px", border: "1.5px solid #e2e8f0", outline: "none", fontSize: "14px", boxSizing: "border-box" },
    label: { display: "block", fontSize: "12px", fontWeight: "800", color: "#a3aed0", marginBottom: "8px", textTransform: "uppercase" },
    btnPrimary: { backgroundColor: "#4f46e5", color: "#fff", padding: "12px 24px", borderRadius: "12px", border: "none", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" },
    btnGhost: { backgroundColor: "#fff", color: "#64748b", padding: "12px 24px", borderRadius: "12px", border: "1.5px solid #e2e8f0", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" },
    badge: (s) => ({ backgroundColor: s === "Active" ? "#dcfce7" : "#fee2e2", color: s === "Active" ? "#166534" : "#dc2626", padding: "5px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: "800" }),
    modalOverlay: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", backdropFilter: "blur(6px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
    modalContent: { backgroundColor: "#fff", borderRadius: "28px", width: "550px", padding: "40px", boxShadow: "0 25px 50px rgba(0,0,0,0.15)" },
    detailRow: { display: "flex", alignItems: "center", gap: "15px", padding: "15px 0", borderBottom: "1px solid #f1f5f9" },
    detailLabel: { width: "120px", color: "#a3aed0", fontWeight: "700", fontSize: "12px", textTransform: "uppercase" },
    detailValue: { flex: 1, color: "#1b2559", fontWeight: "700", fontSize: "15px" },
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        
        {view === "list" ? (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={styles.header}>
              <div>
                <h1 style={styles.title}>Branch Management</h1>
              </div>
              <button style={styles.btnPrimary} onClick={handleOpenCreate}><Plus size={20}/> Add New Branch</button>
            </div>

            <div style={{ display: "flex", gap: "12px", marginBottom: "30px" }}>
              <div style={{ position: "relative", flex: 1, maxWidth: "450px" }}>
                <Search size={18} style={{ position: "absolute", left: "15px", top: "15px", color: "#cbd5e1" }} />
                <input style={{ ...styles.input, paddingLeft: "45px" }} placeholder="Search name, code or city..." value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearchTrigger()} />
              </div>
              <button style={styles.btnPrimary} onClick={handleSearchTrigger}>Search</button>
              {searchTerm && (
                 <button style={styles.btnGhost} onClick={handleClearSearch}><RotateCcw size={16}/> Clear</button>
              )}
            </div>

            <div style={styles.tableWrapper}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={styles.th}>Branch Info</th>
                    <th style={styles.th}>In-Charge</th>
                    <th style={styles.th}>Contact</th>
                    <th style={styles.th}>City</th>
                    <th style={styles.th}>Status</th>
                    <th style={{ ...styles.th, textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" style={{ padding: "80px", textAlign: "center" }}><Loader2 className="animate-spin" /></td></tr>
                  ) : (branches.length === 0 ? (
                    <tr><td colSpan="6" style={{ padding: "40px", textAlign: "center", color: '#a3aed0' }}>No branches found.</td></tr>
                  ) : branches.map(b => (
                    <tr key={b.branchId}>
                      <td style={styles.td}>
                        <div style={{ fontWeight: "800", color: "#1b2559", fontSize: "15px" }}>{b.branchName}</div>
                        <div style={{ fontSize: "12px", color: "#4f46e5", fontWeight: "700" }}>{b.branchCode}</div>
                      </td>
                      <td style={styles.td}><div style={{ fontWeight: "700", color: "#1b2559" }}>{b.branchIncharge || 'N/A'}</div></td>
                      <td style={styles.td}>
                        <div style={{ fontSize: "13px", fontWeight: "600", color: "#1b2559" }}>{b.phoneNumber}</div>
                        <div style={{ fontSize: "12px", color: "#a3aed0" }}>{b.email}</div>
                      </td>
                      <td style={styles.td}><div style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: "700", color: "#64748b" }}><MapPin size={14}/> {b.city}</div></td>
                      <td style={styles.td}><span style={styles.badge(b.status)}>{b.status}</span></td>
                      <td style={{ ...styles.td, textAlign: "center" }}>
                         <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                           <button onClick={() => { setViewData(b); setIsViewModalOpen(true); }} style={{ border: "none", background: "#f1f5f9", padding: "8px", borderRadius: "8px", cursor: "pointer" }}><Eye size={16}/></button>
                           <button onClick={() => handleOpenEdit(b)} style={{ border: "none", background: "#eef2ff", color: "#4f46e5", padding: "8px", borderRadius: "8px", cursor: "pointer" }}><Pencil size={16}/></button>
                           <button onClick={() => handleDelete(b.branchId)} style={{ border: "none", background: "#fef2f2", color: "#ef4444", padding: "8px", borderRadius: "8px", cursor: "pointer" }}><Trash2 size={16}/></button>
                         </div>
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
              <div style={{ padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "13px", color: "#a3aed0", fontWeight: "700" }}>Page {currentPage} of {totalPages}</span>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button disabled={currentPage === 1} onClick={() => setCurrentPage(c => c - 1)} style={styles.btnGhost}><ChevronLeft size={18}/></button>
                  <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(c => c + 1)} style={styles.btnGhost}><ChevronRight size={18}/></button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ================= MANAGE FORM ================= */
          <div style={{ animation: "slideUp 0.4s ease" }}>
            <button style={{ ...styles.btnGhost, marginBottom: "30px" }} onClick={() => setView("list")}><ArrowLeft size={18}/> Back to List</button>
            <div style={{ maxWidth: "800px", margin: "0 auto", backgroundColor: "#fff", padding: "40px", borderRadius: "24px", boxShadow: "0 10px 30px rgba(0,0,0,0.05)" }}>
              <h2 style={{ fontSize: "24px", fontWeight: "900", marginBottom: "30px", color: "#1b2559" }}>{editingId ? "Edit Branch Details" : "Register New Branch"}</h2>
              <form onSubmit={handleFormSubmit}>
                
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "25px", marginBottom: "25px" }}>
                  <div><label style={styles.label}>Branch Name *</label><input style={styles.input} required value={formData.branchName} onChange={(e) => setFormData({ ...formData, branchName: e.target.value })} /></div>
                  <div><label style={styles.label}>Branch Code *</label><input style={styles.input} required value={formData.branchCode} onChange={(e) => setFormData({ ...formData, branchCode: e.target.value })} /></div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "25px", marginBottom: "25px" }}>
                  <div><label style={styles.label}>Branch In-Charge</label><input style={styles.input} value={formData.branchIncharge} onChange={(e) => setFormData({ ...formData, branchIncharge: e.target.value })} /></div>
                  <div><label style={styles.label}>City</label><input style={styles.input} value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} /></div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "25px", marginBottom: "25px" }}>
                  <div><label style={styles.label}>Phone Number</label><input style={styles.input} value={formData.phoneNumber} onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })} /></div>
                  <div><label style={styles.label}>Email Address</label><input type="email" style={styles.input} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
                </div>

                <div style={{ marginBottom: "25px" }}>
                  <label style={styles.label}>Address</label>
                  <textarea style={{ ...styles.input, height: "100px", resize: "none" }} value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                </div>

                <div style={{ marginBottom: "40px", width: "50%" }}>
                    <label style={styles.label}>Status</label>
                    <select style={styles.input} value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "15px", borderTop: "1px solid #f1f5f9", paddingTop: "30px" }}>
                   <button type="button" style={{ ...styles.btnGhost, padding: "12px 35px" }} onClick={() => setView("list")}>Cancel</button>
                   <button type="submit" disabled={saving} style={{ ...styles.btnPrimary, padding: "12px 40px" }}>
                     {saving ? <Loader2 className="animate-spin" size={18}/> : <Save size={18}/>} {saving ? "Saving..." : "Save Branch"}
                   </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ================= VIEW MODAL ================= */}
        {isViewModalOpen && viewData && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
                <h3 style={{ fontSize: "22px", fontWeight: "900", margin: 0, color: "#1b2559" }}>Branch Details</h3>
                <button onClick={() => setIsViewModalOpen(false)} style={{ border: "none", background: "none", cursor: "pointer", color: "#cbd5e1" }}><X size={24}/></button>
              </div>

              <div style={{ display: "flex", flexDirection: "column" }}>
                <div style={styles.detailRow}>
                  <div style={styles.detailLabel}>Branch</div>
                  <div style={styles.detailValue}>
                    <div>{viewData.branchName}</div>
                    <div style={{fontSize:'12px', color:'#4f46e5'}}>{viewData.branchCode}</div>
                  </div>
                </div>
                <div style={styles.detailRow}>
                  <div style={styles.detailLabel}>In-Charge</div>
                  <div style={styles.detailValue}><User size={14} style={{marginRight: '8px'}}/> {viewData.branchIncharge || 'N/A'}</div>
                </div>
                <div style={styles.detailRow}>
                  <div style={styles.detailLabel}>Contact</div>
                  <div style={styles.detailValue}>
                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}><Phone size={14}/> {viewData.phoneNumber || 'N/A'}</div>
                    <div style={{display:'flex', alignItems:'center', gap:'8px', fontSize:'13px', color:'#64748b', marginTop:'4px'}}><Mail size={14}/> {viewData.email || 'N/A'}</div>
                  </div>
                </div>
                <div style={styles.detailRow}>
                  <div style={styles.detailLabel}>Location</div>
                  <div style={styles.detailValue}>
                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}><MapPin size={14}/> {viewData.city}</div>
                    <div style={{fontSize:'13px', color:'#64748b', marginTop:'4px', fontWeight:'500'}}>{viewData.address}</div>
                  </div>
                </div>
                <div style={styles.detailRow}>
                  <div style={styles.detailLabel}>Status</div>
                  <div style={styles.detailValue}><span style={styles.badge(viewData.status)}>{viewData.status}</span></div>
                </div>
              </div>

              <button onClick={() => setIsViewModalOpen(false)} style={{ ...styles.btnPrimary, width: "100%", justifyContent: "center", marginTop: "35px" }}>Close Details</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Branches;